import {Icon} from '@benchling/nucleus-ui';
import {getConversionFactor} from '@benchling/units';
import {StyleSheet, css} from 'aphrodite';
import Decimal from 'decimal.js';
import React, {forwardRef, useImperativeHandle, useState} from 'react';

import BenchlingQuery, {QueryResultType} from 'graphql-client/BenchlingQuery';
import {MAX_FLOAT_DECIMAL_DIGITS} from 'notebook/tables/excess/Util';
import {FieldType, SchemaField} from 'schemas/fields/SchemaFieldConstants';
import {BLUE_LIGHT, BLUE_POWDER_LIGHT, GRAY_LIGHTER, ORANGE, ORANGE_LIGHTEST} from 'ui-library/Colors';
import Menu from 'ui-library/menus/Menu';
import {MenuWidth, OptionContentType} from 'ui-library/menus/MenuConstants';
import Option from 'ui-library/menus/Option';
import {UnitTypesQuery} from 'units/__generated/UnitTypesQuery';
import {UNIT_TYPES_QUERY} from 'units/UnitsConstants';
import {HydratedUnit, hydrateGqlUnit} from 'units/util';
import {isDownKey, isEnterKey, isUpKey} from 'util/Keyboard';
import {DEFAULT_NUMBER_PARSING_FORMAT} from 'util/numbers/NumberParsingFormat';
import {isNumberToParseValid} from 'util/numbers/NumberParsingUtil';
import {ElementKeyboardEvent} from 'util/Types';

function isInputWellFormed(words: Array<string>): boolean {
  return words.length >= 2 && isNumberToParseValid(words[0], DEFAULT_NUMBER_PARSING_FORMAT);
}

export function isUnitConversionPossible(input: string, schemaField: SchemaField | undefined): boolean {
  if (
    !schemaField ||
    !schemaField.unitApiIdentifier ||
    ![FieldType.FLOAT, FieldType.INTEGER].includes(schemaField.fieldType)
  ) {
    return false;
  }

  const inFormulaOrSearch = input.startsWith('@') || input.startsWith('=');
  if (inFormulaOrSearch) {
    return false;
  }

  const words = input.trim().split(' ');
  return isInputWellFormed(words);
}

// exported for testing
export function parseInput(
  input: string,
  allUnits: Array<HydratedUnit>,
  fieldUnit: HydratedUnit,
  integerOnly: boolean
): {
  matchingUnit: HydratedUnit | null;
  newValue: string | null;
  incompatibleUnit: boolean;
  roundedToInt: boolean;
} {
  const emptyRes = {
    matchingUnit: null,
    newValue: null,
    incompatibleUnit: false,
    roundedToInt: false,
  };
  const words = input.trim().split(' ');
  if (!isInputWellFormed(words)) {
    return emptyRes;
  }
  const matchingUnit = allUnits.find((unit) => unit.symbol === words.slice(1).join(' '));
  if (!matchingUnit) {
    return emptyRes;
  }
  if (matchingUnit.unitType.id !== fieldUnit.unitType.id) {
    return {...emptyRes, matchingUnit, incompatibleUnit: true};
  }

  const factor = getConversionFactor(matchingUnit, fieldUnit);
  const inputValue = new Decimal(words[0]);
  const rawNewValue = inputValue.times(factor);

  if (integerOnly && rawNewValue.decimalPlaces() > 0) {
    return {...emptyRes, matchingUnit, newValue: rawNewValue.toNearest(1).toString(), roundedToInt: true};
  }

  // We round here so the precision will match that of the evaluated cell when the conversion is accepted
  const newValue = rawNewValue.toPrecision(Math.min(MAX_FLOAT_DECIMAL_DIGITS, rawNewValue.precision()));

  return {
    ...emptyRes,
    matchingUnit,
    newValue,
  };
}

function InfoMessage({text}: {text: string}): JSX.Element {
  return (
    <div className={css(styles.message, styles.infoMessage)}>
      <Icon style={{color: BLUE_LIGHT, marginRight: '4px'}} icon="info-circle" />
      {text}
    </div>
  );
}
function WarningMessage({text}: {text: string}): JSX.Element {
  return (
    <div className={css(styles.message, styles.warningMessage)}>
      <Icon style={{color: ORANGE, marginRight: '4px'}} icon="exclamation-triangle" />
      {text}
    </div>
  );
}

export interface UnitConversionDropdownHandle {
  handleKeydown: (e: ElementKeyboardEvent) => boolean;
}

interface DropdownProps {
  input: string;
  unitId: string;
  onSelect: (text: string) => void;
  integerOnly: boolean;
}
function UnitConversionDropdownMenuInner(
  props: DropdownProps & {allUnits: Array<HydratedUnit>},
  ref: React.Ref<UnitConversionDropdownHandle>
): JSX.Element | null {
  const {onSelect, allUnits, unitId, input, integerOnly} = props;
  const [isActive, setIsActive] = useState(false);
  const fieldUnit = allUnits.find((unit) => unit.id === unitId)!;
  const maybeMatch = parseInput(input, allUnits, fieldUnit, integerOnly);

  useImperativeHandle(
    ref,
    () => ({
      handleKeydown(e: ElementKeyboardEvent): boolean {
        if (!maybeMatch.newValue) {
          return false;
        }
        if (isDownKey(e)) {
          if (!isActive) {
            setIsActive(true);
          }
          e.preventDefault();
          return true;
        }
        if (isUpKey(e)) {
          if (isActive) {
            setIsActive(false);
          }
          e.preventDefault();
          return true;
        }
        if (isEnterKey(e) && isActive) {
          onSelect(maybeMatch.newValue);
          e.preventDefault();
          return true;
        }
        return false;
      },
    }),
    [isActive, onSelect, maybeMatch]
  );

  if (!maybeMatch.matchingUnit) {
    return null;
  }

  return (
    <Menu className={css(styles.menu)} width={MenuWidth.SMALL} disableKeyboardAccess={true}>
      <Option
        contentClassName={css(styles.optionContent)}
        contentType={OptionContentType.PLAIN}
        onMouseUp={() => {
          if (maybeMatch.newValue) {
            onSelect(maybeMatch.newValue);
          }
        }}
        isHovered={isActive}
        onHover={() => {
          setIsActive(true);
        }}
        onMouseOut={() => {
          setIsActive(false);
        }}
      >
        {maybeMatch.incompatibleUnit && (
          <WarningMessage text={`Cannot convert ${maybeMatch.matchingUnit.symbol} to ${fieldUnit.symbol}`} />
        )}
        {maybeMatch.roundedToInt && <InfoMessage text="Rounded to nearest integer" />}
        {maybeMatch.newValue && (
          <div className="u-flex-display">
            <div className={css(styles.conversionPreviewWrapper)}>
              <div>=</div>
              <div className={css(styles.conversionPreview)}>
                {maybeMatch.newValue} {fieldUnit.symbol}
              </div>
            </div>
          </div>
        )}
      </Option>
    </Menu>
  );
}

export const UnitConversionDropdownMenu = forwardRef(UnitConversionDropdownMenuInner);
export default function QueryingUnitConversionDropdownMenu(
  props: DropdownProps,
  ref: React.Ref<UnitConversionDropdownHandle>
): JSX.Element {
  return (
    <BenchlingQuery<UnitTypesQuery> query={UNIT_TYPES_QUERY} renderLoading={() => {}}>
      {(data, result) => {
        if (result.type !== QueryResultType.SUCCESS) {
          return null;
        }
        const allUnits = data.unitTypes.flatMap((unitType) =>
          unitType.units.map((unit) => hydrateGqlUnit(unit, unitType))
        );

        return <UnitConversionDropdownMenu {...props} allUnits={allUnits} ref={ref} />;
      }}
    </BenchlingQuery>
  );
}

const styles = StyleSheet.create({
  conversionPreview: {
    padding: '2px 4px',
    borderRadius: '2px',
    backgroundColor: GRAY_LIGHTER,
    marginLeft: '8px',
  },
  conversionPreviewWrapper: {
    marginLeft: 'auto',
    marginRight: '4px',
    paddingLeft: '4px',
    display: 'flex',
    alignItems: 'center',
  },
  optionContent: {
    padding: 0,
  },
  menu: {
    marginTop: '-10px',
    marginLeft: '-6px',
  },
  message: {
    marginTop: '-8px',
    padding: '6px',
    fontSize: '11px',
    display: 'flex',
    alignItems: 'center',
  },
  infoMessage: {
    backgroundColor: BLUE_POWDER_LIGHT,
    marginBottom: '8px',
    borderRadius: '4px 4px 0 0',
  },
  warningMessage: {
    backgroundColor: ORANGE_LIGHTEST,
    marginBottom: '-8px',
    borderRadius: '4px',
  },
});
